import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from 'react-aria-components';
import { cn } from './lib/cn';

export type ButtonVariant = 'pill' | 'primary' | 'ghost' | 'menu' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonShape = 'default' | 'circle';

export type ButtonStyleOptions = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  className?: string;
};

export type ButtonProps = Omit<AriaButtonProps, 'className'> &
  ButtonStyleOptions;

const BASE = [
  /* The user-agent button styling, undone: this package ships no Preflight. */
  'sk:m-0 sk:appearance-none sk:border-0 sk:bg-transparent sk:font-sans sk:cursor-pointer',
  'sk:inline-flex sk:items-center sk:justify-center sk:gap-1.5 sk:whitespace-nowrap',
  'sk:text-dense sk:font-medium sk:tracking-dense',
  'sk:transition-[background-color,box-shadow,border-color,color] sk:duration-75',
  /* React Aria's `data-focus-visible`, not the CSS pseudo-class: only it knows
     whether the keyboard was what moved focus. */
  'sk:outline-none sk:rac-focus-visible:outline-2 sk:rac-focus-visible:outline-solid sk:rac-focus-visible:outline-offset-1 sk:rac-focus-visible:outline-primary',
  'sk:disabled:cursor-not-allowed sk:disabled:opacity-50',
].join(' ');

/* `sm` is the size inside a table row; controls standing on their own are `md`. */
const SIZES: Record<ButtonSize, string> = {
  sm: 'sk:h-6 sk:px-2.5',
  md: 'sk:h-7 sk:px-3',
  /* Matches a segmented control, for the controls beside one. */
  lg: 'sk:h-7.5 sk:px-3',
};

/* `primary` takes its foreground from `surface`, which inverts with the theme. */
const VARIANTS: Record<ButtonVariant, string> = {
  pill: [
    'sk:rounded-full sk:inset-shadow-hairline sk:bg-surface sk:text-fg-secondary sk:shadow-card',
    'sk:hover:bg-fill-quaternary sk:hover:text-fg',
    'sk:rac-pressed:bg-fill-secondary sk:rac-pressed:text-fg sk:rac-pressed:shadow-none',
    'sk:rac-selected:bg-fill-secondary sk:rac-selected:text-fg sk:rac-selected:shadow-none',
  ].join(' '),
  primary:
    'sk:rounded-full sk:bg-primary sk:text-surface sk:hover:opacity-90 sk:rac-pressed:opacity-80',
  ghost:
    'sk:rounded-full sk:text-fg-secondary sk:hover:bg-fill-quaternary sk:hover:text-fg sk:rac-pressed:bg-fill-secondary',
  menu: [
    'sk:h-9 sk:w-full sk:justify-start sk:rounded-option sk:px-3 sk:text-fg-secondary',
    'sk:hover:bg-fill-quaternary sk:hover:text-fg sk:rac-pressed:bg-fill-secondary',
  ].join(' '),
  danger: [
    'sk:rounded-full sk:border sk:border-solid sk:border-danger sk:text-danger',
    'sk:hover:bg-danger-bg sk:rac-pressed:bg-danger-bg',
  ].join(' '),
};

const SHAPES: Record<ButtonShape, string> = {
  default: '',
  circle: 'sk:aspect-square sk:rounded-full sk:px-0',
};

/**
 * The class string behind `Button`, exported so other React Aria components can
 * wear the same skin. The `rac-selected:` states are already in `pill`.
 */
export function buttonStyles({
  variant = 'pill',
  size = 'md',
  shape = 'default',
  className,
}: ButtonStyleOptions = {}): string {
  return cn(BASE, SIZES[size], VARIANTS[variant], SHAPES[shape], className);
}

export function Button({
  variant,
  size,
  shape,
  className,
  ...props
}: ButtonProps) {
  return (
    <AriaButton
      {...props}
      className={buttonStyles({ variant, size, shape, className })}
    />
  );
}
